/**
 * Alive Water - live Lottie animations for the "Why choose" benefit carousel
 * (2026-08-18, Brian: restore the original per-card animations).
 *
 * HISTORY. The pre-7/16 order page animated each benefit card with a Lottie
 * JSON (loader: alive-benefits-lottie.js on theme "Updated Pricing Table");
 * when the content was ported to the new theme the cards were captured as
 * FROZEN first frames (snippets/svg-*.liquid still carry __lottie_element clip
 * ids from that capture). This file replays the ORIGINAL animation files on
 * top of those captures.
 *
 * MECHANICS. Each host is a [data-aw-lottie] wrapper the section renders
 * around a frozen snippet. The frozen svg stays as the instant, no-JS, and
 * reduced-motion fallback. On approach (IntersectionObserver, 300px margin)
 * the loader fetches the JSON and builds the animation HIDDEN in a sibling
 * box inside the snippet's own sizing wrapper, then swaps frozen -> live in
 * one frame once the renderer reports DOMLoaded - the two draw the same
 * artwork at the same size, so the swap is invisible. Animations play only
 * while on screen (12 simultaneous loops would cost real CPU; the original
 * slick page paused off-screen slides for the same reason).
 *
 * The lottie-web library self-loads from cdnjs on first need, exactly like
 * alive-waterfall-gsap.js does for GSAP - no library weight for visitors who
 * never scroll here.
 */
(function () {
  'use strict';

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var LIB = 'https://cdnjs.cloudflare.com/ajax/libs/lottie-web/5.12.2/lottie.min.js';
  var libPromise = null;

  function loadLib() {
    if (window.lottie) return Promise.resolve();
    if (!libPromise) {
      libPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = LIB;
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
    }
    return libPromise;
  }

  function build(host) {
    if (host.__awAnim || host.__awBuilding) return;
    host.__awBuilding = true;
    // Most cards froze as inline SVG captures; foundation + vortexer froze as
    // PNG <img> captures. Either works as the box the animation replaces.
    var frozen = host.querySelector('svg, img');
    var src = host.getAttribute('data-aw-lottie');
    // No frozen frame to size against, or no source: leave the card alone.
    if (!frozen || !src) return;
    var container = frozen.parentElement;

    loadLib().then(function () {
      if (!window.lottie || !host.isConnected || host.__awAnim) return;
      var box = document.createElement('div');
      box.className = 'aw-anim__live';
      box.style.display = 'none';
      container.appendChild(box);
      var anim = window.lottie.loadAnimation({
        container: box,
        renderer: 'svg',
        loop: true,
        autoplay: false,
        path: src,
        rendererSettings: { preserveAspectRatio: 'xMidYMid meet', progressiveLoad: true }
      });
      anim.addEventListener('DOMLoaded', function () {
        box.style.display = '';
        if (frozen.parentNode) frozen.parentNode.removeChild(frozen);
        if (host.__awVisible) anim.play();
      });
      anim.addEventListener('data_failed', function () {
        // Keep the frozen frame; never leave an empty card.
        box.remove();
        host.__awAnim = null;
      });
      host.__awAnim = anim;
    }).catch(function () { /* library blocked: frozen frames remain */ });
  }

  function boot() {
    var hosts = document.querySelectorAll('[data-aw-lottie]');
    if (!hosts.length) return;

    if (!('IntersectionObserver' in window)) {
      // Ancient engine: build everything, play everything.
      for (var i = 0; i < hosts.length; i++) {
        (function (h) {
          h.__awVisible = true;
          build(h);
        })(hosts[i]);
      }
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var host = e.target;
        host.__awVisible = e.isIntersecting;
        if (e.isIntersecting) {
          build(host);
          if (host.__awAnim && host.__awAnim.isLoaded) host.__awAnim.play();
        } else if (host.__awAnim && host.__awAnim.isLoaded) {
          host.__awAnim.pause();
        }
      });
    }, { rootMargin: '300px 0px' });

    for (var j = 0; j < hosts.length; j++) io.observe(hosts[j]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
